import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { GroupBy } from "app/data/helpers";
import {
  mapReportsFilterDataToFormValues,
  mergeReportObjectOptions,
  reportsCommonFormInitialValues
} from "app/data/helpers/ReportHelpers";
import { useAppDispatch, useAppSelector, useSelectSearch } from "hooks";
import {
  selectAccountGroups,
  selectDrawerSelectedAccountId,
  selectObjectsState,
  selectObjectTypesState,
  selectQueriedObjectsState,
  selectReportsState,
  selectSearchedMinimalAccountsState
} from "app/data/selectors";
import {
  getAllAccountGroupsThunk,
  getAllBasicInfoObjectsThunk,
  getObjectsByQueryThunk,
  getObjectTypesByAccountThunk,
  resetReports,
  resetReportsFilterData,
  resetReportsDateValues,
  searchMinimalAccountsThunk
} from "app/features";
import { IReportsFilterData } from "app/features/reports/reportsSlice";
import { DateTime } from "luxon";
import { ObjectsService } from "app/data/services";
import { toast } from "@vilocnv/allsetra-core";
import { useTranslation } from "react-i18next";

const useReportCommonStates = () => {
  const dispatch = useAppDispatch();
  const isFetching = useRef(false);
  const previousAccountIdRef = useRef<string | null>(null);
  const preloadGenerationRef = useRef(0);
  const { t } = useTranslation();

  // Global State
  const drawerSelectedAccountId = useAppSelector(selectDrawerSelectedAccountId);

  const { searchedMinimalAccounts, searchedMinimalAccountsLoading } =
    useAppSelector(selectSearchedMinimalAccountsState);
  const { objectTypes, loading: objectTypesLoading } = useAppSelector(
    selectObjectTypesState
  );

  const { objects, loading: queriedObjectsLoading } = useAppSelector(
    selectQueriedObjectsState
  );

  const {
    basicObjects,
    initialObjectsCount,
    preloadedObjects,
    loading: objectsListLoading
  } = useAppSelector(selectObjectsState);

  const { accountGroups } = useAppSelector(selectAccountGroups);

  const {
    report,
    loading: reportsLoading,
    reportsFilterData,
    reportsHasExceededLimit,
    reportsDateValues
  } = useAppSelector(selectReportsState);

  // Local State
  const [filterOpen, setFilterOpen] = useState(false);

  const [dateFormat, setDateFormat] = useState<GroupBy>("day");

  const [skrrsObjects, setSkrrsObjects] = useState<any[]>([]);
  const [skrrsLoading, setSkrrsLoading] = useState<boolean>(false);

  // Initialize dateValues from reportsDateValues if available
  const [dateValues, setDateValues] = useState(() => {
    if (reportsDateValues?.startDate && reportsDateValues?.endDate) {
      return {
        startDate: new Date(reportsDateValues.startDate),
        endDate: new Date(reportsDateValues.endDate)
      };
    }
    // Default to today in local timezone
    const today = DateTime.now();
    return {
      startDate: today.toJSDate(),
      endDate: today.toJSDate()
    };
  });

  const [localReport, setLocalReport] = useState<any>([]);

  // Hydrate filterValues from Redux reportsFilterData
  const [filterValues, setFilterValues] = useState(() =>
    mapReportsFilterDataToFormValues(
      reportsFilterData as IReportsFilterData,
      reportsCommonFormInitialValues
    )
  );

  // Initialize selectedLocalIds from reportsFilterData if available
  const [selectedLocalIds, setSelectedLocalIds] = useState(() => {
    const filterData = reportsFilterData as IReportsFilterData;
    if (filterData?.object && Array.isArray(filterData.object)) {
      return filterData.object;
    }
    if (filterData?.objectIds && Array.isArray(filterData.objectIds)) {
      return filterData.objectIds;
    }
    return [];
  });

  const objectsForSelection = useMemo(
    () =>
      mergeReportObjectOptions({
        queried: objects,
        preloaded: basicObjects
      }),
    [objects, basicObjects]
  );

  const selectedLocalObjects = useSelectSearch(
    selectedLocalIds,
    objectsForSelection
  );

  // Full account object list (like keys) + keep selected/queried for safety
  const mergedObjectOptions = useMemo(
    () =>
      mergeReportObjectOptions({
        selected: selectedLocalObjects,
        queried: objects,
        preloaded: basicObjects?.length ? basicObjects : preloadedObjects
      }),
    [selectedLocalObjects, objects, basicObjects, preloadedObjects]
  );

  const getHydratedFormInitialValues = useCallback(
    (baseInitialValues: Record<string, any>) =>
      mapReportsFilterDataToFormValues(
        reportsFilterData as IReportsFilterData,
        baseInitialValues
      ),
    [reportsFilterData]
  );

  const handleOnChangeReportFilterForm = (val?: any) => {
    setSelectedLocalIds(Array.isArray(val) ? val : []);
  };

  const resetReportDates = useCallback(() => {
    const today = DateTime.now().toJSDate();
    const resetDates = { startDate: today, endDate: today };
    setDateValues(resetDates);
    dispatch(resetReportsDateValues());
  }, [dispatch]);

  const handleObjectsSearch = useCallback(
    (value: string) => {
      dispatch(
        getObjectsByQueryThunk({
          accountId: drawerSelectedAccountId || "",
          params: {
            itemsPerPage: 50,
            page: 1,
            where: [{ field: "name", value, type: 0 }]
          },
          minimal: true
        })
      );
    },
    [drawerSelectedAccountId]
  );

  const handleSkrrsObjectsSearch = useCallback(
    async (value: string) => {
      try {
        setSkrrsLoading(true);
        const response = await ObjectsService.getMinimalSkrrsObjects({
          accountId: drawerSelectedAccountId || "",
          params: {
            itemsPerPage: 50,
            page: 1,
            where: [{ field: "name", value, type: 0 }]
          }
        });
        if (response.status === 200) {
          setSkrrsObjects(response?.data?.results || []);

          if (response?.data?.results?.length === 0) {
            toast.info(t("typography.searchSkrrsObjects"));
          }
        }
      } catch (e) {
        console.error("Failed to fetch SKRRS objects", e);
      } finally {
        setSkrrsLoading(false);
      }
    },
    [drawerSelectedAccountId]
  );

  const handleAccountSearch = useCallback((value: string) => {
    dispatch(
      searchMinimalAccountsThunk({
        itemsPerPage: 100,
        page: 1,
        where: [{ field: "name", value, type: 0 }]
      })
    );
  }, []);

  // Always load the full basic object list for report dropdowns (same UX as keys).
  // Stale responses for a previous account are ignored in the objects reducer via
  // basicObjectsRequestedAccountId.
  const preloadInitialObjects = async (accountId: string) => {
    const generation = ++preloadGenerationRef.current;
    isFetching.current = true;

    await dispatch(getAllBasicInfoObjectsThunk(accountId));

    if (generation === preloadGenerationRef.current) {
      isFetching.current = false;
    }
  };

  useEffect(() => {
    const previous = previousAccountIdRef.current;
    previousAccountIdRef.current = drawerSelectedAccountId;

    // Skip initial mount — only reset on actual account switch
    if (
      previous &&
      drawerSelectedAccountId &&
      previous !== drawerSelectedAccountId
    ) {
      dispatch(resetReportsFilterData());
      dispatch(resetReports());
      setSelectedLocalIds([]);
      setFilterValues(reportsCommonFormInitialValues);
      // Allow fetches for the new account even if a previous preload is in-flight
      isFetching.current = false;
    }

    if (isFetching.current === false) {
      preloadInitialObjects(drawerSelectedAccountId || "");
      dispatch(getAllAccountGroupsThunk(drawerSelectedAccountId || ""));
      dispatch(getObjectTypesByAccountThunk(drawerSelectedAccountId || ""));
    }
  }, [drawerSelectedAccountId]);

  // Update local state when reportsDateValues changes
  useEffect(() => {
    if (reportsDateValues?.startDate && reportsDateValues?.endDate) {
      // Create dates in UTC to avoid timezone issues
      const startDate = new Date(reportsDateValues.startDate);
      const endDate = new Date(reportsDateValues.endDate);

      // Adjust to local timezone
      setDateValues({
        startDate,
        endDate
      });
    }
  }, [reportsDateValues]);

  // Update local state when reportsFilterData changes
  useEffect(() => {
    const filterData = reportsFilterData as IReportsFilterData;
    setFilterValues(
      mapReportsFilterDataToFormValues(
        filterData,
        reportsCommonFormInitialValues
      )
    );

    if (filterData?.object && Array.isArray(filterData.object)) {
      setSelectedLocalIds(filterData.object);
    } else if (filterData?.objectIds && Array.isArray(filterData.objectIds)) {
      setSelectedLocalIds(filterData.objectIds);
    } else if (!filterData || Object.keys(filterData).length === 0) {
      setSelectedLocalIds([]);
    }
  }, [reportsFilterData]);

  return {
    drawerSelectedAccountId,
    objectTypes,
    objectTypesLoading,
    objects,
    objectsLoading: objectsListLoading || queriedObjectsLoading,
    accountGroups,
    report,
    reportsLoading,
    filterOpen,
    setFilterOpen,
    dateFormat,
    setDateFormat,
    dateValues,
    setDateValues,
    localReport,
    setLocalReport,
    handleObjectsSearch,
    selectedLocalObjects,
    selectedLocalIds,
    setSelectedLocalIds,
    handleOnChangeReportFilterForm,
    resetReportDates,
    basicObjects,
    initialObjectsCount,
    reportsFilterData,
    reportsHasExceededLimit,
    preloadedObjects,
    mergedObjectOptions,
    getHydratedFormInitialValues,
    filterValues,
    setFilterValues,
    handleAccountSearch,
    searchedMinimalAccounts,
    searchedMinimalAccountsLoading,
    handleSkrrsObjectsSearch,
    skrrsObjects,
    skrrsLoading
  };
};

export default useReportCommonStates;
